import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Typography, TextField, Button, Box, Link, Alert } from '@mui/material';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';

interface LoginFormValues {
  email: string;
  password: string;
}

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  
  // Get returnUrl from query params if available
  // Default is root path (which is now the About page)
  const returnUrl = searchParams.get('returnUrl') || '/';
  
  // Check for messages from navigation state
  useEffect(() => {
    if (location.state && location.state.message) {
      if (location.state.severity === 'error') {
        setError(location.state.message);
      } else {
        setInfoMessage(location.state.message);
      }
    }
  }, [location]);

  const initialValues: LoginFormValues = {
    email: '',
    password: '',
  };

  const handleSubmit = async (
    values: LoginFormValues,
    { setSubmitting }: FormikHelpers<LoginFormValues>
  ) => {
    try {
      setError(null);
      setInfoMessage(null);
      console.log('Attempting login with:', values.email);
      await login(values.email, values.password);
      console.log('Login successful');
      
      // Navigate to returnUrl or home page after successful login
      navigate(returnUrl);
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.response) {
        console.error('Response error:', err.response);
        setError(err.response.data?.detail || 'Login failed. Please check your credentials.');
      } else if (err.request) {
        console.error('Request error (no response):', err.request);
        setError('Network error. Server may be unreachable.');
      } else {
        console.error('Other error:', err.message);
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#121212',
      position: 'relative',
    }}>
      {/* Logo in top right */}
      <Box sx={{ 
        position: 'absolute',
        top: 20,
        right: 20,
      }}>
        <img 
          src="/images/EcoActionAI_WhiteBlueRev_RGB.png"
          alt="EcoActionAI Logo"
          style={{ maxWidth: '150px' }}
        />
      </Box>
      
      <Box className="auth-form-container">
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          EcoActionAI Login
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {infoMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {infoMessage}
          </Alert>
        )}
        
        <Formik
          initialValues={initialValues}
          validationSchema={LoginSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Email
                </Typography>
                <Field
                  as={TextField}
                  fullWidth
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                  InputProps={{ style: { color: '#fff' } }}
                />
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Password
                </Typography>
                <Field
                  as={TextField}
                  fullWidth
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                  InputProps={{ style: { color: '#fff' } }}
                />
              </Box>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={isSubmitting}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {isSubmitting ? 'Logging in...' : 'Log In'}
              </Button>
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link 
                  component={RouterLink} 
                  to="/forgot-password" 
                  sx={{ color: '#6AB7FF' }}
                >
                  Forgot password?
                </Link>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" display="inline">
                    Don't have an account?{' '}
                  </Typography>
                  <Link 
                    component={RouterLink} 
                    to="/signup" 
                    sx={{ color: '#6AB7FF' }}
                  >
                    Sign up
                  </Link>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default LoginPage;
